import React, { useId } from 'react'
import Ikona from './IkonaZlecenia'
import { formatPola, kwotaZPola, podgladBudzetu } from '../../lib/zakresBudzetu'

/**
 * „Budżet na całość” z kwotą „około” i paskiem „Co zobaczą wykonawcy” (makieta 12, D15). Kwota jest opcjonalna.
 * `zwykle`: zwykłe ceny takiej instalacji z estymatora (`{od, do, zestaw_cena?, tylkoMontaz?, zrodlo?}`) albo null; bez
 * nich pasek pokazuje zakres z samego budżetu. Zakres wiążący liczy backend przy wystawieniu (`lib/zakresBudzetu`).
 *
 * Nad polem blok „Ile może kosztować” (2026-10-29): widełki były tylko w zwiniętym pasku i opisane jako to, co zobaczą
 * wykonawcy, a nie jako koszt. `podstawa` mówi, co o instalacji wiemy na pewno (moc, magazyn, sprzęt); gdy czegoś
 * brakuje, to „Wstępny szacunek” z listą braków, bo doradzamy pewnie dopiero przy pełnym opisie. Rodzaju dachu nie
 * wymagamy: bez niego dolna granica jest liczona jak na blasze, górna jak na gruncie (właściciel 2026-10-29).
 */
const Tekst = ({ czesci }) => czesci.map((c, i) => (typeof c === 'string' ? c : <b key={i}>{c.b}</b>))
const zl = (n) => `${Math.round(n).toLocaleString('pl-PL')} zł`
const liczba = (n) => String(n).replace('.', ',')

/**
 * Co wiemy o instalacji: lista `{tekst, jest, pewny}`, w kolejności wpływu na cenę. `pewny` tylko dla wartości podanych
 * wprost; moc z rachunku i magazyn dobrany przez estymator są znane, ale przybliżone.
 */
const elementyPodstawy = (p) => {
  if (!p) return []
  const magazynZnany = p.magazyn_kwh === 0 || Boolean(p.magazyn_kwh)
  let magazyn = 'pojemność magazynu'
  if (magazynZnany) magazyn = p.magazyn_kwh ? (p.magazynDobrany ? `magazyn ok. ${liczba(p.magazyn_kwh)} kWh (dobrany)` : `magazyn ${liczba(p.magazyn_kwh)} kWh`) : 'bez magazynu'
  return [
    { klucz: 'moc', jest: Boolean(p.kwp), pewny: Boolean(p.kwp) && !p.kwpZRachunku,
      tekst: !p.kwp ? 'moc' : p.kwpZRachunku ? `moc ok. ${liczba(p.kwp)} kWp (dobrana)` : `${liczba(p.kwp)} kWp` },
    { klucz: 'magazyn', jest: magazynZnany, pewny: magazynZnany && !p.magazynDobrany, tekst: magazyn },
    ...(p.dach ? [{ klucz: 'dach', jest: true, pewny: true, tekst: `dach: ${String(p.dach).toLowerCase()}` }] : []),
    { klucz: 'sprzet', jest: true, pewny: true, tekst: p.sprzet ? 'Twój zestaw' : 'typowy zgodny zestaw' },
  ]
}

/** „moc i magazyn” */
const lista = (xs) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} i ${xs[xs.length - 1]}`)

const IleKosztuje = ({ zwykle, podstawa }) => {
  const montaz = Boolean(zwykle.tylkoMontaz)
  const elementy = elementyPodstawy(podstawa)
  const braki = elementy.filter((e) => !e.jest).map((e) => e.tekst)
  const przyblizone = elementy.some((e) => e.jest && !e.pewny)
  const pewny = elementy.length > 0 && elementy.every((e) => e.pewny)
  const z = zwykle.zrodlo || {}
  const sklepy = Array.isArray(z.sklepy) ? z.sklepy.length : 0
  return (
    <div className="ile" data-testid="ile-kosztuje">
      <div className="ile-glowa">
        <span>{montaz ? 'Ile może kosztować montaż' : 'Ile może kosztować taka instalacja'}</span>
        {elementy.length > 0 && (
          <span className={`ile-pewnosc${pewny ? '' : ' wstepny'}`}>{pewny ? 'Szacunek dla Twojej instalacji' : 'Wstępny szacunek'}</span>
        )}
      </div>
      <p className="ile-kwota num">
        {zwykle.od.toLocaleString('pl-PL')} do {zl(zwykle.do)}{' '}
        <small>brutto{montaz ? '' : ' z montażem'}, przed dotacją</small>
      </p>
      {elementy.length > 0 && (
        <ul className="ile-podstawa" aria-label="Na podstawie">
          {elementy.map((e) => (
            <li key={e.klucz} className={e.jest ? '' : 'brak'}>
              {e.jest && <Ikona nazwa="ok" rozmiar={13} grubosc={2.6} />}{e.tekst}
            </li>
          ))}
        </ul>
      )}
      {braki.length > 0 && <p className="ile-brak">Podaj {lista(braki)}, a szacunek będzie dokładniejszy.</p>}
      {!braki.length && przyblizone && (
        <p className="ile-brak">Część danych szacujemy z Twoich odpowiedzi. Z Projektem 3D albo konkretnym sprzętem policzymy dokładniej.</p>
      )}
      <p className="ile-zrodlo">
        {montaz && zwykle.zestaw_cena ? `Zestaw kupujesz osobno (w sklepach ok. ${zl(zwykle.zestaw_cena)}). ` : ''}
        Najtańszy zgodny zestaw z ofert{sklepy ? ` ${sklepy} sklepów` : ' sklepów'}, montaż, konstrukcja i koszty firmy.
        {podstawa && !podstawa.dach ? ' Bez rodzaju dachu: od montażu na blasze do montażu na gruncie.' : ''}
      </p>
    </div>
  )
}

const BudzetZlecenia = ({ wartosc, onZmiana, zwykle = null, zOdpowiedzi = false, podstawa = null }) => {
  const id = useId()
  const kwota = kwotaZPola(wartosc)
  const p = podgladBudzetu(kwota, zwykle)
  const zestaw = Boolean(zwykle?.zestaw_cena)
  const maWidelki = Boolean(zwykle?.od && zwykle?.do)

  return (
    <fieldset className="grupa">
      <legend>Budżet na całość <small>(opcjonalnie)</small></legend>
      {maWidelki && <IleKosztuje zwykle={zwykle} podstawa={podstawa} />}
      <p className="grupa-opis">
        Kwota brutto za sprzęt, montaż i formalności, przed odjęciem dotacji. Wystarczy „około”. Zakres dla wykonawców
        policzymy sami.
      </p>
      <label className="pole budzet-pole" htmlFor={`${id}-k`}>Około
        <span className="kwota-wejscie">
          <input className="wejscie" id={`${id}-k`} inputMode="numeric" autoComplete="off" placeholder="np. 46 000"
                 value={wartosc} onChange={(e) => onZmiana(e.target.value, true)}
                 onBlur={(e) => onZmiana(formatPola(kwotaZPola(e.target.value)), false)} />
          <span aria-hidden="true">zł</span>
        </span>
      </label>
      {zOdpowiedzi && <p className="budzet-skad">Wpisane z Twoich odpowiedzi. Możesz zmienić albo usunąć.</p>}
      {/* Przy wpisanej kwocie rada jest od razu pod polem, nie w zwiniętym pasku (2026-10-29). */}
      {kwota > 0 && (
        <p className={`budzet-rada${p.uwaga ? ' uwaga' : ''}`} aria-live="polite">
          {p.uwaga && <Ikona nazwa="uwaga" rozmiar={15} grubosc={2.4} className="skala-wykrzyknik" />}
          <span><Tekst czesci={p.tekst} /></span>
        </p>
      )}
      <details className="skala">
        <summary>
          <span>Co zobaczą wykonawcy</span>
          <span className={`skala-wartosc${p.uwaga ? ' uwaga' : ''}`}>{p.wartosc}</span>
          <Ikona nazwa="dol" rozmiar={16} grubosc={2.4} />
        </summary>
        <div className="skala-tresc" aria-live="polite">
          {p.pasy && (
            <>
              <div className="skala-pas" aria-hidden="true">
                {p.pasy.rynek && <span className="skala-rynek" style={p.pasy.rynek} />}
                {p.pasy.wykonawcy && <span className="skala-firmy" style={p.pasy.wykonawcy} />}
                {p.pasy.znak && <span className="skala-znak" style={{ left: p.pasy.znak }} />}
              </div>
              <div className="skala-legenda" aria-hidden="true">
                {p.pasy.rynek && <span><i style={{ background: 'var(--line-2)' }} />Zwykłe ceny{zestaw ? ' montażu' : ''}</span>}
                {p.pasy.wykonawcy && <span><i style={{ background: '#10B981' }} />Zakres dla wykonawców</span>}
                {p.pasy.znak && <span><i style={{ background: 'var(--fg)' }} />{zestaw ? 'Twoja kwota na montaż' : 'Twój budżet'}</span>}
              </div>
            </>
          )}
          {!kwota && (
            <p className="skala-tekst">
              <Tekst czesci={p.tekst} />
            </p>
          )}
        </div>
      </details>
    </fieldset>
  )
}

export default BudzetZlecenia
